import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "../../modal/ConfirmDialog";
import { useMemberProfile } from "../MemberProfileContext";
import { ActivateForm } from "./ActivateForm";
import { ArchiveForm } from "./ArchiveForm";
import { EditSectionForm } from "./EditSectionForm";
import { FamilyMemberForm } from "./FamilyMemberForm";
import { FamilyView } from "./FamilyView";
import { LinkForm } from "./LinkForm";
import { MembershipForm } from "./membership/MembershipForm";
import { OnboardingByPhoneConfirm } from "./OnboardingByPhoneConfirm";
import type { ProfileOverlay } from "./profileOverlay";
import { ReferralForm } from "./ReferralForm";

/** Matches the slide duration so a closing panel is gone once it is off screen. */
const SLIDE_MS = 300;

export function ProfileOverlays() {
    const { overlay } = useMemberProfile();
    // The closing panel stays mounted until it has slid back out to the right.
    const [shown, setShown] = useState(overlay);

    useEffect(() => {
        if (overlay) {
            setShown(overlay);
            return;
        }

        const timer = setTimeout(() => setShown(null), SLIDE_MS);
        return () => clearTimeout(timer);
    }, [overlay]);

    if (!shown) return null;

    return (
        <div
            className={cn(
                "absolute inset-0 z-10 duration-300",
                overlay
                    ? "fade-in slide-in-from-right animate-in"
                    : "fade-out slide-out-to-right animate-out fill-mode-forwards",
            )}
        >
            <OverlayContent overlay={shown} />
        </div>
    );
}

function OverlayContent({ overlay }: { overlay: ProfileOverlay }) {
    const {
        profile,
        setOverlay,
        deleteLimitedFamily,
        deleteLinkedFamily,
        deleteLink,
        saving,
    } = useMemberProfile();

    const onClose = () => setOverlay(null);

    switch (overlay.type) {
        case "edit":
            return <EditSectionForm section={overlay.section} onClose={onClose} />;
        case "membership":
            return (
                <MembershipForm
                    action={overlay.action}
                    onClose={onClose}
                    onOpen={setOverlay}
                />
            );
        case "family-view":
            return (
                <FamilyView
                    member={overlay.member}
                    profile={profile}
                    onClose={onClose}
                    onEdit={() =>
                        setOverlay({
                            type: "family-form",
                            kind: overlay.member.kind,
                            member: overlay.member,
                        })
                    }
                    onDelete={() =>
                        setOverlay({ type: "family-delete", member: overlay.member })
                    }
                />
            );
        case "family-form":
            return (
                <FamilyMemberForm
                    kind={overlay.kind}
                    member={overlay.member}
                    onClose={onClose}
                />
            );
        case "family-delete":
            return (
                <ConfirmDialog
                    open
                    title={
                        overlay.member.kind === "linked"
                            ? "Remove link"
                            : "Delete family member"
                    }
                    description={
                        overlay.member.kind === "linked"
                            ? `Remove the dual membership link to ${overlay.member.name}?`
                            : `Delete ${overlay.member.name} from this profile?`
                    }
                    confirmLabel={
                        overlay.member.kind === "linked" ? "Remove link" : "Delete"
                    }
                    confirmDisabled={saving}
                    onConfirm={() => {
                        if (overlay.member.kind === "linked") {
                            if (overlay.member.linkedMemberId) {
                                deleteLinkedFamily(
                                    {
                                        memberIdToUnlink: overlay.member.linkedMemberId,
                                    },
                                    onClose,
                                );
                            } else {
                                onClose();
                            }
                        } else {
                            deleteLimitedFamily(overlay.member.id, onClose);
                        }
                    }}
                    onDismiss={onClose}
                />
            );
        case "link-form":
            return <LinkForm link={overlay.link} onClose={onClose} />;
        case "link-delete":
            return (
                <ConfirmDialog
                    open
                    title="Delete link"
                    description={`Delete ${overlay.link.title}?`}
                    confirmLabel="Delete"
                    confirmDisabled={saving}
                    onConfirm={() => {
                        deleteLink(overlay.link.id, onClose);
                    }}
                    onDismiss={onClose}
                />
            );
        case "referral":
            return <ReferralForm promotion={overlay.promotion} onClose={onClose} />;
        case "onboarding-by-phone":
            return (
                <OnboardingByPhoneConfirm enable={overlay.enable} onClose={onClose} />
            );
        case "archive":
            return <ArchiveForm onClose={onClose} />;
        case "activate":
            return <ActivateForm onClose={onClose} />;
        default: {
            const _exhaustive: never = overlay;
            return _exhaustive;
        }
    }
}
